import { forwardRef, type ElementType, type HTMLAttributes, type Ref } from "react";
import styled from "@emotion/styled";
import type { CSSObject } from "@emotion/react";

export type TextVariant =
  | "display"
  | "h1"
  | "h2"
  | "h3"
  | "title"
  | "body"
  | "body-sm"
  | "body-xs"
  | "caption"
  | "label";
export type TextTone = "primary" | "secondary" | "muted" | "disabled" | "danger" | "success" | "accent";
export type TextWeight = "normal" | "medium" | "semibold" | "bold";

interface TextOwnerState {
  variant: TextVariant;
  tone: TextTone;
  weight: TextWeight | null;
}

/* Exact compiled output of the pre-migration Tailwind utilities in cva order
 * (base → variant → tone → weight), verified against the frozen baseline.
 * Cascade notes:
 * - Every variant lists its `leading-*` AFTER the size class, so tw-merge kept
 *   both; the leading utilities sit LATER in the compiled stylesheet than the
 *   text-* sizes, so each variant's net line-height comes from its leading-*.
 * - display: the `sm:bfrs-text-6xl` media rule sits later in the stylesheet
 *   than `leading-[1.1]`, so at ≥640px BOTH font-size and line-height come
 *   from text-6xl (3.75rem / 1).
 * - label: `tracking-[0.16em]` came after the base `tracking-normal` in the
 *   class list, so tw-merge dropped the base; the variant's letterSpacing
 *   overrides the base here the same way.
 * - weight is applied after variant (and tone), so its font-weight wins. */

const textBase: CSSObject = {
  letterSpacing: "0em"
};

const textVariantStyles: Record<TextVariant, CSSObject> = {
  display: {
    fontSize: "3rem",
    fontWeight: 700,
    lineHeight: "1.1",
    "@media (min-width:640px)": {
      fontSize: "3.75rem",
      lineHeight: "1"
    }
  },
  h1: { fontSize: "2.25rem", fontWeight: 700, lineHeight: "1.25" },
  h2: { fontSize: "1.875rem", fontWeight: 600, lineHeight: "1.25" },
  h3: { fontSize: "1.5rem", fontWeight: 600, lineHeight: "1.375" },
  title: { fontSize: "1.25rem", fontWeight: 600, lineHeight: "1.375" },
  body: { fontSize: "1rem", fontWeight: 400, lineHeight: "1.75rem" },
  "body-sm": { fontSize: ".875rem", fontWeight: 400, lineHeight: "1.45" },
  "body-xs": { fontSize: "12px", fontWeight: 400, lineHeight: "1.4" },
  caption: { fontSize: ".75rem", fontWeight: 400, lineHeight: "1.5" },
  label: {
    fontSize: "10px",
    fontWeight: 700,
    textTransform: "uppercase",
    lineHeight: "1.5",
    letterSpacing: ".16em"
  }
};

const textToneStyles: Record<TextTone, CSSObject> = {
  primary: {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-primary) / var(--tw-text-opacity, 1))"
  },
  secondary: {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-secondary) / var(--tw-text-opacity, 1))"
  },
  muted: {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-muted) / var(--tw-text-opacity, 1))"
  },
  disabled: {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-text-disabled) / var(--tw-text-opacity, 1))"
  },
  danger: {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-danger) / var(--tw-text-opacity, 1))"
  },
  success: {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-success) / var(--tw-text-opacity, 1))"
  },
  accent: {
    "--tw-text-opacity": "1",
    color: "hsl(var(--bfrs-color-accent) / var(--tw-text-opacity, 1))"
  }
};

const textWeightStyles: Record<TextWeight, CSSObject> = {
  normal: { fontWeight: 400 },
  medium: { fontWeight: 500 },
  semibold: { fontWeight: 600 },
  bold: { fontWeight: 700 }
};

/* `as` is excluded from shouldForwardProp so Emotion consumes it and swaps the
 * rendered tag while keeping the styles (defaultTagByVariant behavior). */
const TextRoot = styled("p", {
  label: "AgenticText-root",
  shouldForwardProp: (propName) => propName !== "ownerState" && propName !== "as"
})<{ ownerState: TextOwnerState; as?: ElementType }>(({ ownerState }) => [
  textBase,
  textVariantStyles[ownerState.variant],
  textToneStyles[ownerState.tone],
  ownerState.weight !== null && textWeightStyles[ownerState.weight]
]);

export type TextProps = HTMLAttributes<HTMLElement> & {
  as?: ElementType;
  variant?: TextVariant | null;
  tone?: TextTone | null;
  weight?: TextWeight | null;
};

const defaultTagByVariant: Record<TextVariant, ElementType> = {
  display: "h1",
  h1: "h1",
  h2: "h2",
  h3: "h3",
  title: "p",
  body: "p",
  "body-sm": "p",
  "body-xs": "p",
  caption: "p",
  label: "span"
};

export const Text = forwardRef<HTMLElement, TextProps>(({ as, className, variant, tone, weight, ...props }, ref) => {
  const resolvedVariant = variant ?? "body";
  const Component = as ?? defaultTagByVariant[resolvedVariant];
  return (
    <TextRoot
      as={Component}
      ref={ref as Ref<HTMLParagraphElement>}
      ownerState={{ variant: resolvedVariant, tone: tone ?? "primary", weight: weight ?? null }}
      className={className}
      {...props}
    />
  );
});

Text.displayName = "Text";
